import { Col, Typography } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DatabaseReference } from "firebase/database";
import AutoSizer, { Size } from "react-virtualized-auto-sizer";
import { FixedSizeList as List, ListChildComponentProps } from "react-window";

import { RootState } from "../../../app/store";
import UserRequestDTO from "../../../shared/dtos/userRequestDTO";
import UserRequestCard from "./userRequestCard";
import { USER_REQUEST_CARD_ITEM_SIZE } from "./userRequestCard/constants";
import { DispatcherMap } from "../../map/Map";
import getDestinationsByUserRequestHelper from "../../../utils/helpers/getDestinationsByUserRequestHelper";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { WebRoutes } from "../../../shared/constants/WebRoutes";
import { updateUserRequestValue } from "../../../features/userRequest/pendingUserRequestSlice";
import { useNavigate } from "react-router-dom";
import MapDTO from "../../../shared/dtos/mapDTO";
import ProviderDTO from "../../../shared/dtos/providerDTO";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import FilterBeta from "../create/utils/filterComponentBeta";

import ContainerLayout from "../../common/containerLayout";

import "../../../shared/styles/dark.css";
import "./styles.css";
import {
  subscribeDriverTracking,
  subscribeTripRecords,
  unSubscribe,
} from "../../../utils/externals/firebase/firebaseDB";
import { getProviderById } from "../../../services/adminProviderApi";
import { filterUserRequestsBySearch } from "./utils/userRequestSearch";

const { Title, Text } = Typography;

interface UserRequestListItemData {
  activeTrips: UserRequestDTO[];
  selectedTrips: UserRequestDTO[];
  handleDoubleClick: (
    event: React.MouseEvent,
    userRequest: UserRequestDTO,
  ) => void;
  handleSelectedTrips: (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    userRequest: UserRequestDTO,
    add: boolean,
  ) => void;
}

interface UserRequestDataMapProps {
  directions?: MapDTO | null;
  provider?: ProviderDTO | null;
  bookingId?: string | null;
}

const renderUserRequestRow = ({
  index,
  style,
  data,
}: ListChildComponentProps<UserRequestListItemData>) => {
  const { activeTrips, selectedTrips, handleDoubleClick, handleSelectedTrips } =
    data;

  const userRequest = activeTrips[index];

  if (!userRequest) {
    return null;
  }

  return (
    <div style={style} onDoubleClick={(e) => handleDoubleClick(e, userRequest)}>
      <UserRequestCard
        userRequest={userRequest}
        isChecked={selectedTrips.some((i) => i.id === userRequest.id)}
        onSelect={handleSelectedTrips}
      />
    </div>
  );
};

let driversTrackingRef: DatabaseReference | null = null;

const UserRequestListBeta: React.FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [userRequestDataMap, setUserRequestDataMap] =
    useState<UserRequestDataMapProps | null>(null);
  const [activeProviders, setActiveProviders] = useState<Array<ProviderDTO>>(
    [],
  );
  const [selectedTrips, setSelectedTrips] = useState<UserRequestDTO[]>([]);
  const [activeTrips, setActiveTrips] = useState<UserRequestDTO[]>([]);
  const [activeTripsLoading, setActiveTripsLoading] = useState<boolean>(false);
  const [cardSearchQuery, setCardSearchQuery] = useState("");
  const [selectedProvider, setSelectedProvider] = useState<ProviderDTO | null>(
    null,
  );

  const { selectedServiceTypeId } = useAppSelector(
    (state: RootState) => state.serviceTypeList,
  );

  useEffect(() => {
    let unsubscribe: () => void = () => {};
    setActiveTripsLoading(true);
    const init = async () => {
      unsubscribe = subscribeTripRecords((trips) => {
        setActiveTrips(trips);
        setActiveTripsLoading(false);
      });
    };

    init();

    return () => unsubscribe();
  }, []);

  const fetchProviderData = async () => {
    if (!selectedProvider || selectedProvider.id) return;

    const provider = await getProviderById(selectedProvider?.providerId!);

    if (provider) setSelectedProvider((prev) => ({ ...prev, ...provider }));
  };

  useEffect(() => {
    fetchProviderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvider]);

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent, userRequest: UserRequestDTO) => {
      if (!event.altKey || userRequest.providerId) return;

      dispatch(updateUserRequestValue([userRequest]));
      navigate(WebRoutes.AssignProviderRoute, {
        replace: true,
      });
    },
    [dispatch, navigate],
  );

  useEffect(() => {
    setActiveProviders([]);
    if (driversTrackingRef) unSubscribe(driversTrackingRef);
    if (!selectedTrips.length)
      driversTrackingRef = subscribeDriverTracking(setActiveProviders);
  }, [selectedTrips, selectedServiceTypeId]);

  const map = useMemo(
    () => (
      <DispatcherMap
        destinations={userRequestDataMap?.directions}
        activeProvider={userRequestDataMap?.provider || null}
        providers={activeProviders}
        showProviders={!!activeProviders.length}
        bookingId={userRequestDataMap?.bookingId}
        setProvider={setSelectedProvider}
        selectedProvider={selectedProvider}
      />
    ),
    [
      userRequestDataMap?.directions,
      userRequestDataMap?.provider,
      userRequestDataMap?.bookingId,
      activeProviders,
      selectedProvider,
    ],
  );

  const handleSelectedTrips = useCallback(
    (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>,
      userRequest: UserRequestDTO,
      add: boolean,
    ) => {
      setUserRequestDataMap({
        directions: getDestinationsByUserRequestHelper(userRequest),
        provider: userRequest.provider,
        bookingId: userRequest.bookingId,
      });
      if (event.altKey)
        setSelectedTrips((userRequests) => {
          if (add) {
            return [...userRequests, userRequest];
          } else {
            return userRequests.filter((r) => r.id !== userRequest.id);
          }
        });
      else setSelectedTrips([userRequest]);
    },
    [],
  );

  const filteredTrips = useMemo(
    () => filterUserRequestsBySearch(activeTrips, cardSearchQuery),
    [activeTrips, cardSearchQuery],
  );

  const listItemData = useMemo(
    () => ({
      activeTrips: filteredTrips,
      selectedTrips,
      handleDoubleClick,
      handleSelectedTrips,
    }),
    [filteredTrips, selectedTrips, handleDoubleClick, handleSelectedTrips],
  );

  return (
    <ContainerLayout
      title="Listado de viajes"
      showNotification={false}
      showSound
    >
      <Col span={24}>
        <FilterBeta
          selectedTrips={selectedTrips}
          cardSearchQuery={cardSearchQuery}
          onCardSearchChange={setCardSearchQuery}
          totalTripsCount={activeTrips.length}
          filteredTripsCount={filteredTrips.length}
        />
      </Col>
      {activeTripsLoading && !activeTrips?.length && <Col span={8}></Col>}
      {!!activeTrips?.length && (
        <Col span={8} className="user-request-list-column">
          {filteredTrips.length > 0 ? (
            <div
              className="card-container"
              id="provider"
              style={{ overflow: "hidden" }}
            >
              <AutoSizer>
                {({ height, width }: Size) => (
                  <List
                    height={height}
                    width={width}
                    itemCount={filteredTrips.length}
                    itemSize={USER_REQUEST_CARD_ITEM_SIZE}
                    itemData={listItemData}
                    itemKey={(index, data) =>
                      data.activeTrips[index]?.id ?? index
                    }
                    overscanCount={3}
                  >
                    {renderUserRequestRow}
                  </List>
                )}
              </AutoSizer>
            </div>
          ) : (
            <div className="card-container user-request-card-search-empty">
              <ExclamationCircleOutlined />
              <Title level={4}>Sin coincidencias</Title>
              <Text type="secondary">
                No hay tarjetas que coincidan con &quot;{cardSearchQuery}&quot;
              </Text>
            </div>
          )}
        </Col>
      )}
      {!activeTripsLoading && !activeTrips?.length && (
        <Col span={8} className="loading-container error-container">
          <ExclamationCircleOutlined />
          <Title level={3}>No hay datos</Title>
        </Col>
      )}
      <Col span={16}>{map}</Col>
    </ContainerLayout>
  );
};

export default UserRequestListBeta;
